import React, {Component, createRef, cloneElement} from 'react';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {withRouter} from 'react-router-dom';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import classNames from 'classnames';
import {getWindow} from 'ssr-window';
import {routeToSpotDetails} from 'router/router-utils';
import {push} from 'store/router/router-actions';
import {setSpotDetailReferrer} from 'store/spot/spot-actions';

const window = getWindow();

class SearchLayoutDesktop extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        SearchMap: PropTypes.object,
        SpotDetails: PropTypes.object,
        SpotList: PropTypes.object,
        SpotListSearchControls: PropTypes.object,
        SpotListSearchControlsCondensed: PropTypes.object,
        SpotListSorter: PropTypes.object,
        EventList: PropTypes.object,
        isEvent: PropTypes.bool,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        selectedSpotId: PropTypes.number,
        showEventList: PropTypes.bool,
        disableKeydownEvents: PropTypes.bool,
        setSpotDetailReferrerTo: PropTypes.func,
        pushTo: PropTypes.func,
    };

    state = {
        showCondensedControls: false,
    };
    _listRef = createRef();

    componentDidMount() {
        this._searchControlsEl = DOMUtils.el('.SpotListSearchControls');
        this._onListThrottle = throttle(this._onListScroll, 500);

        if (!this.props.disableKeydownEvents) {
            window.addEventListener('keydown', this._onKeyDown);
        }
    }

    componentDidUpdate(prevProps) {
        const {selectedSpotId} = this.props;
        const {selectedSpotId: prevSelectedSpotId} = prevProps;

        if (selectedSpotId && selectedSpotId !== prevSelectedSpotId) {
            // if a new active spot is set, scroll the list to its position
            const item = DOMUtils.el(`.result${selectedSpotId}`);

            if (item) {
                AnimationUtils.scrollTo(
                    this._listRef.current,
                    item.offsetTop,
                    250,
                    'y',
                    () => {
                        DOMUtils.removeClass(item, 'highlight'); // remove it first in case it existed

                        // a short delay ensures the css animation repeats
                        setTimeout(() => {
                            DOMUtils.addClass(item, 'highlight');
                        }, 150);
                    }
                );
            }
        }
    }

    componentWillUnmount() {
        if (this._onListThrottle) {
            this._onListThrottle.cancel();
        }

        if (!this.props.disableKeydownEvents) {
            window.removeEventListener('keydown', this._onKeyDown);
        }
    }

    _onListScroll = () => {
        this.setState({
            showCondensedControls:
                this._listRef.current.scrollTop >
                this._searchControlsEl.offsetHeight,
        });
    };

    _onEditClick = () => {
        AnimationUtils.scrollTo(this._listRef.current, 0);
    };

    _onKeyDown = evt => {
        const {
            history: {location},
            spots,
            selectedSpotId,
            pushTo,
            setSpotDetailReferrerTo,
        } = this.props;

        if (!selectedSpotId) {
            return;
        }

        const index = spots.findIndex(({spotId}) => spotId === selectedSpotId);
        let nextSpot;

        if (evt.which === 40) {
            evt.preventDefault();

            nextSpot = spots[index + 1];
        } else if (evt.which === 38) {
            evt.preventDefault();

            nextSpot = spots[index - 1];
        }

        if (nextSpot) {
            setSpotDetailReferrerTo('list');
            routeToSpotDetails({
                method: pushTo,
                location,
                spotId: nextSpot.spotId,
                historyState: {referrer: 'list'},
            });
        }
    };

    render() {
        const {
            SearchMap,
            SpotDetails,
            SpotList,
            SpotListSorter,
            SpotListSearchControls,
            SpotListSearchControlsCondensed,
            EventList,
            spots,
            selectedSpotId,
            isEvent,
            showEventList,
        } = this.props;

        const {showCondensedControls} = this.state;
        const classes = classNames({
            'SpotListContainer-show-condensed-controls': showCondensedControls,
        });
        const numSpots = spots.length;
        // this is taking the component passed in with all its props, cloning it and adding the onEditClick prop
        // Cloning is required to keep existing props and add an additional prop
        const CondensedControls = cloneElement(
            SpotListSearchControlsCondensed,
            {
                onEditClick: this._onEditClick,
            }
        );

        return (
            <main className="search-results">
                <div className="SearchMap-container">{SearchMap}</div>
                <div className={classes}>
                    <div
                        ref={this._listRef}
                        className="SpotListContainer"
                        itemScope={isEvent ? true : null}
                        itemType={isEvent ? 'http://schema.org/Event' : null}
                        onScroll={this._onListThrottle}
                    >
                        {SpotListSearchControls}
                        {numSpots > 0 && SpotListSorter}
                        {SpotList}
                    </div>
                    {CondensedControls}
                </div>
                {showEventList && EventList}
                {Boolean(selectedSpotId) && SpotDetails}
            </main>
        );
    }
}

const mapDispatchToProps = {
    pushTo: push,
    setSpotDetailReferrerTo: setSpotDetailReferrer,
};

const enhance = compose(withRouter, connect(null, mapDispatchToProps));

export default enhance(SearchLayoutDesktop);
